import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { WhoIsItFor } from "../../components/Home/WhoIsItFor";
import { SeoTags } from "../../components/Head/SeoTags";
import { AITagging, Encryption, MultiCollection } from "../Features/Callout";
import { ExploreMore } from "../../components/Home/ExploreMore";
import { PrivateAI } from "../../components/Home/PrivateAI";
import ExtensionSection from "./ExtensionSection";

const Home = () => {
  const { t } = useTranslation();
  // Respect the visitor's reduced-motion preference and, more importantly,
  // keep the hero mockup legible on first paint (it is the LCP element) rather
  // than fading it in from zero over a full second.
  const reduce = useReducedMotion();

  return (
    <>
      <SeoTags
        title={t("home.seo.title")}
        description={t("home.seo.description")}
        keywords={t("home.seo.keywords")}
      />

      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "onBookmarks",
            url: "https://onbookmarks.com",
            logo: "https://onbookmarks.com/images/icons/logo-only.svg",
            description: t("home.seo.description"),
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer support",
              email: "support@onbookmarks.com"
            }
          }),
        }}
      />

      {/* WebSite Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "onBookmarks",
            url: "https://onbookmarks.com",
            description: t("home.seo.description"),
            potentialAction: {
              "@type": "SearchAction",
              target: "https://my.onbookmarks.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }),
        }}
      />

      {/* SoftwareApplication Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "onBookmarks",
            applicationCategory: "ProductivityApplication",
            operatingSystem: "Web, iOS, Android",
            offers: [
              {
                "@type": "Offer",
                name: "Free Plan",
                price: "0",
                priceCurrency: "USD",
                description: "Free bookmark manager with basic features"
              },
              {
                "@type": "Offer",
                name: "Pro Plan",
                price: "3",
                priceCurrency: "USD",
                description: "Advanced bookmark manager with AI features and unlimited spaces"
              }
            ],
            description: t("home.seo.description"),
            featureList: [
              t("home.howItWorks.steps.step1.title"),
              t("home.howItWorks.steps.step2.title"),
              t("home.howItWorks.steps.step3.title")
            ]
          }),
        }}
      />

      {/* HowTo Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: t("home.howItWorks.title"),
            description: t("home.howItWorks.description"),
            step: [
              {
                "@type": "HowToStep",
                name: t("home.howItWorks.steps.step1.title"),
                text: t("home.howItWorks.steps.step1.description")
              },
              {
                "@type": "HowToStep",
                name: t("home.howItWorks.steps.step2.title"),
                text: t("home.howItWorks.steps.step2.description")
              },
              {
                "@type": "HowToStep",
                name: t("home.howItWorks.steps.step3.title"),
                text: t("home.howItWorks.steps.step3.description")
              }
            ],
            totalTime: "PT5M"
          }),
        }}
      />

      <section className="home-hero position-relative py-6">
        <div className="hero-bg"></div>
        <div className="hero-grid"></div>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="hero-badge">
                {t("home.hero.badge", "The private AI bookmark manager")}
              </div>
              <h1 className="display-3 fw-bold mb-4 text-white text-center text-lg-start">
                {t("home.hero.title")}
              </h1>
              <p className="lead fw-medium mb-4 text-white text-center text-lg-start">
                {t("home.hero.description")}
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 align-items-center align-items-lg-start justify-content-center justify-content-lg-start">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-lg btn-white fw-semibold px-4"
                >
                  {t("home.hero.cta")}
                </Link>
                <a
                  href="#how-it-works"
                  className="btn btn-lg btn-outline-light px-4"
                >
                  {t("home.hero.secondaryCta", "See how it works")}
                </a>
              </div>
              <p className="small text-white-50 mt-3 mb-0 text-center text-lg-start">
                {t("home.hero.noCreditCard", "No credit card required")}
              </p>
            </div>
            <div className="col-lg-6 position-relative mt-5 mt-lg-0">
              {/* One clear "saved & auto-tagged" moment replaces the old
                  overlapping brand-tile cluster — legible in half a second and
                  it shows exactly what the product does when you save a link. */}
              <motion.div
                className="hero-save-card rounded-4"
                // Slide in only (never fade from 0): this is the hero's LCP
                // visual and must be painted even if the tab loads backgrounded.
                initial={reduce ? { y: 0 } : { y: 16 }}
                animate={{ y: 0 }}
                transition={{ duration: reduce ? 0 : 0.45, ease: "easeOut" }}
              >
                <div className="hero-save-head">
                  <span className="hero-save-fav">A</span>
                  <div className="flex-grow-1 min-w-0">
                    <div className="hero-save-title">Claude Code Docs</div>
                    <div className="hero-save-url">docs.anthropic.com</div>
                  </div>
                  <span className="hero-save-check" aria-hidden="true">
                    <i className="bi bi-check-lg"></i>
                  </span>
                </div>

                <div className="hero-save-label">Auto-tagged</div>
                <div className="hero-tag-row">
                  {["Development", "AI Tools", "Docs"].map((tag) => (
                    <span className="hero-tag" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="hero-save-divider"></div>

                <div className="hero-save-label">Added to collections</div>
                <div className="hero-tag-row">
                  {["Claude Code", "AI Resources"].map((tag) => (
                    <span className="hero-tag" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="how-it-works"
        className="py-5 pt-lg-5 bg-light rounded-bottom-4 overflow-hidden position-relative"
      >
        <div className="container">
          <div className="text-center">
            <h2 className="display-5 fw-bold mb-4">
              {t("home.howItWorks.title")}
            </h2>
            <p className="lead">{t("home.howItWorks.description")}</p>
          </div>
          <div className="row position-relative align-items-stretch g-4 mt-0 mb-5">
            {[
              { num: 1, icon: "bi-bookmark", key: "step1" },
              { num: 2, icon: "bi-robot", key: "step2" },
              { num: 3, icon: "bi-shield-lock", key: "step3" },
            ].map((step) => (
              <div className="col-md-4" key={step.key}>
                <div className="card how-step-card shadow-none border-0 bg-white p-4 h-100">
                  <div className="mt-2 mb-3 d-flex align-items-center">
                    <span className="how-step-num">{step.num}</span>
                    <i className={`bi ${step.icon} fs-4 d-flex text-primary ms-3`}></i>
                    <h3 className="h4 mb-0 ms-2 fw-semibold">
                      {t(`home.howItWorks.steps.${step.key}.title`)}
                    </h3>
                  </div>
                  <p className="text-muted mb-0">
                    {t(`home.howItWorks.steps.${step.key}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ExtensionSection />

      <section id="features" className="container my-5">
        <div className="row">
          <div className="col-xl-10 col-lg-8 mx-auto col-12">
            <div className="d-flex flex-column text-center">
              <div>
                <h2 className="display-5 fw-bold mb-3">
                  {t("home.features.title")}
                </h2>
                <p className="lead mb-0">{t("home.features.description")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MultiCollection />

      <Encryption />

      <AITagging />

      <PrivateAI />

      <WhoIsItFor />

      <ExploreMore />

      {/* Trust strip — honest, verifiable signals (no ratings, no competitor
          callouts) that reinforce the "private and smart" positioning right
          before the final call to action. */}
      <section className="py-5">
        <div className="container">
          <p className="text-center text-primary fw-semibold mb-4">
            {t("home.trust.badge", "Private by design")}
          </p>
          <div className="row g-4 justify-content-center text-center">
            {[
              {
                icon: "bi-shield-lock",
                label: t("home.trust.encryption", "Client-side encryption"),
              },
              {
                icon: "bi-eye-slash",
                label: t("home.trust.noTracking", "No ads, no tracking"),
              },
              {
                icon: "bi-box-arrow-up",
                label: t("home.trust.export", "Export everything, anytime"),
              },
              {
                icon: "bi-arrow-repeat",
                label: t("home.trust.sync", "Syncs across every browser"),
              },
            ].map((item) => (
              <div className="col-6 col-lg-3" key={item.label}>
                <div className="d-flex flex-column align-items-center gap-2">
                  <i className={`bi ${item.icon} fs-3 text-primary`}></i>
                  <span className="fw-medium text-muted">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding banner-gradient">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8 mx-auto text-center">
              <h3 className="h2 fw-bold mb-4 text-white">
                {t("home.cta.title")}
              </h3>
              <p className="lead mb-4 fw-medium text-white">
                {t("home.cta.description")}
              </p>
              <Link
                to={`${process.env.REACT_APP_APP_URL}/register`}
                className="btn btn-white py-3 px-5 fw-bold mt-4"
              >
                {t("home.cta.button")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
